import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { X, Save, Eye, Printer, Plus, Trash2, Search, Download, Mail, Send, Unlock, Ban, CircleDollarSign } from 'lucide-react';
import { invoicesAPI, serviceCallsAPI } from '@shared/apiClient';
import InventorySearch from './InventorySearch';
import toast from 'react-hot-toast';
import {
  downloadInvoicePdf,
  fetchInvoicePreviewHtml,
  printInvoicePreview
} from '../utils/invoiceActions';
import { computeInvoiceTotals } from '@shared/salesTaxUtils';

const LINE_ITEM_CATEGORIES = [
  { value: 'labor', label: 'Labor' },
  { value: 'parts', label: 'Parts' },
  { value: 'other', label: 'Other' }
];

const LINE_ITEM_TAXABLE_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'taxable', label: 'Taxable' },
  { value: 'exempt', label: 'Exempt' }
];

const LOCKED_STATUSES = new Set(['sent', 'paid', 'void']);

const formatMoney = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
}).format(Number(value || 0));

const emptyLineItem = () => ({
  itemNumber: '',
  code: '',
  description: '',
  unitPrice: 0,
  quantity: 1,
  category: 'other',
  taxable: null
});

export default function CspireInvoiceEditor({ serviceCall, isOpen, onClose, inline = false, onPrinted }) {
  const open = inline ? true : isOpen;
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(null);
  const [showInventorySearch, setShowInventorySearch] = useState(false);
  const [inventoryResetKey, setInventoryResetKey] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showSendModal, setShowSendModal] = useState(false);
  const [recipientInput, setRecipientInput] = useState('');
  const [showContactSendModal, setShowContactSendModal] = useState(false);
  const [unlockEdit, setUnlockEdit] = useState(false);
  const [suggestingNumber, setSuggestingNumber] = useState(false);

  // The C Spire contact captured off the inbound WO email — "Opened By" when
  // present, else whatever "<Verb> By:" contact was on file (see
  // backend/services/cspireEmailParser.js). This is who techs/admins should
  // be able to send the finished invoice straight back to.
  const workOrder = serviceCall?.cspireWorkOrder;
  const contactName = workOrder?.openedByName || null;
  const contactEmail = workOrder?.openedByEmail || null;
  const contactPhone = workOrder?.openedByPhone || null;

  const {
    data: invoiceData,
    isLoading,
    error: invoiceError,
    isError
  } = useQuery(
    ['invoice', serviceCall?.id],
    () => invoicesAPI.getByServiceCall(serviceCall.id).then((res) => res.data.invoice),
    { enabled: open && !!serviceCall?.id, retry: false }
  );

  const needsCreate = isError && invoiceError?.response?.status === 404;

  const createMutation = useMutation(
    () => invoicesAPI.createInvoice(serviceCall.id),
    {
      onSuccess: (res) => {
        queryClient.setQueryData(['invoice', serviceCall.id], res.data.invoice);
        setFormData(JSON.parse(JSON.stringify(res.data.invoice)));
        toast.success('Invoice created');
      },
      onError: (error) => toast.error(error.response?.data?.message || 'Failed to create invoice')
    }
  );

  useEffect(() => {
    if (invoiceData) {
      setFormData(JSON.parse(JSON.stringify(invoiceData)));
      setUnlockEdit(false);
    }
  }, [invoiceData]);

  useEffect(() => {
    if (open && needsCreate && !createMutation.isLoading && !formData) {
      createMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, needsCreate]);

  const saveMutation = useMutation(
    (payload) => invoicesAPI.updateInvoice(formData.id, payload),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['invoice', serviceCall.id]);
        queryClient.invalidateQueries(['serviceCall', serviceCall.id]);
        queryClient.invalidateQueries('serviceCalls');
        queryClient.invalidateQueries('priorityServiceCalls');
        queryClient.invalidateQueries('regularServiceCalls');
        queryClient.invalidateQueries('invoicingQueue');
        queryClient.invalidateQueries(['invoiceFolderItems']);
        setFormData(JSON.parse(JSON.stringify(res.data.invoice)));
        setUnlockEdit(false);
        toast.success('Invoice saved');
      },
      onError: (error) => toast.error(error.response?.data?.message || 'Failed to save invoice')
    }
  );

  const statusMutation = useMutation(
    (status) => invoicesAPI.updateInvoice(formData.id, { status }),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['invoice', serviceCall.id]);
        queryClient.invalidateQueries(['serviceCall', serviceCall.id]);
        queryClient.invalidateQueries(['invoiceFolderItems']);
        setFormData(JSON.parse(JSON.stringify(res.data.invoice)));
        toast.success(`Invoice marked ${res.data.invoice.status}`);
      },
      onError: (error) => toast.error(error.response?.data?.message || 'Failed to update status')
    }
  );

  const sendMutation = useMutation(
    (toEmails) => invoicesAPI.sendInvoice(formData.id, toEmails),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['invoice', serviceCall.id]);
        queryClient.invalidateQueries(['serviceCall', serviceCall.id]);
        queryClient.invalidateQueries(['invoiceFolderItems']);
        setFormData(JSON.parse(JSON.stringify(res.data.invoice)));
        setShowSendModal(false);
        toast.success('Invoice sent');
      },
      onError: (error) => toast.error(error.response?.data?.message || 'Failed to send invoice')
    }
  );

  const recipientsQuery = useQuery(
    ['invoiceRecipients', formData?.id],
    () => invoicesAPI.getRecipients(formData.id).then((res) => res.data.recipients),
    { enabled: showSendModal && !!formData?.id }
  );

  useEffect(() => {
    if (showSendModal && recipientsQuery.data?.length) {
      setRecipientInput(recipientsQuery.data.join(', '));
    }
  }, [showSendModal, recipientsQuery.data]);

  const subtotal = useMemo(() => {
    if (!formData?.lineItems) return 0;
    return formData.lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 1);
    }, 0);
  }, [formData?.lineItems]);

  const taxEnabled = formData?.taxEnabled !== false;
  const taxRate = formData?.taxRate != null ? Number(formData.taxRate) : 0.07;
  const taxCategoryRules = formData?.taxCategoryRules || {
    laborTaxable: true,
    partsTaxable: true,
    otherTaxable: true
  };
  const totals = useMemo(() => computeInvoiceTotals(formData?.lineItems || [], {
    taxEnabled,
    taxRate,
    taxCategoryRules
  }), [formData?.lineItems, taxEnabled, taxRate, taxCategoryRules]);
  const taxAmount = totals.taxAmount;
  const grandTotal = totals.total;

  const financiallyLocked = formData ? LOCKED_STATUSES.has(formData.status) : false;
  const fieldsLocked = financiallyLocked && !unlockEdit;
  const isVoid = formData?.status === 'void';

  if (!open || !serviceCall) return null;

  if ((isLoading || createMutation.isLoading || (needsCreate && !formData)) && !formData) {
    const loadingBody = (
      <div className={inline ? 'p-8 text-center text-gray-600' : 'relative top-20 mx-auto p-8 border w-full max-w-md shadow-lg rounded-md bg-white text-center text-gray-600'}>
        {needsCreate ? 'Creating invoice...' : 'Loading invoice...'}
      </div>
    );
    if (inline) return loadingBody;
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-[60]">
        {loadingBody}
      </div>
    );
  }

  if (!formData) {
    const errBody = (
      <div className={inline ? 'p-8 text-center text-gray-600' : 'relative top-20 mx-auto p-8 border w-full max-w-md shadow-lg rounded-md bg-white text-center'}>
        <p className="text-gray-700 mb-3">Invoice could not be loaded.</p>
        <button
          type="button"
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded"
          onClick={() => createMutation.mutate()}
        >
          Create invoice
        </button>
      </div>
    );
    if (inline) return errBody;
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-[60]">
        {errBody}
      </div>
    );
  }

  const updateField = (field, value) => {
    if (fieldsLocked && field !== 'invoiceNumber') return;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateSiteField = (field, value) => {
    if (fieldsLocked) return;
    setFormData((prev) => ({
      ...prev,
      siteLocation: { ...prev.siteLocation, [field]: value }
    }));
  };

  const updateBillToLine = (index, value) => {
    if (fieldsLocked) return;
    setFormData((prev) => {
      const billTo = [...(prev.billTo || [])];
      billTo[index] = value;
      return { ...prev, billTo };
    });
  };

  const addBillToLine = () => {
    if (fieldsLocked) return;
    setFormData((prev) => ({ ...prev, billTo: [...(prev.billTo || []), ''] }));
  };

  const removeBillToLine = (index) => {
    if (fieldsLocked) return;
    setFormData((prev) => ({
      ...prev,
      billTo: (prev.billTo || []).filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index, field, value) => {
    if (fieldsLocked) return;
    setFormData((prev) => {
      const lineItems = [...prev.lineItems];
      lineItems[index] = { ...lineItems[index], [field]: value };
      return { ...prev, lineItems };
    });
  };

  const addLineItem = () => {
    if (fieldsLocked) return;
    setFormData((prev) => ({
      ...prev,
      lineItems: [...(prev.lineItems || []), emptyLineItem()]
    }));
  };

  const removeLineItem = (index) => {
    if (fieldsLocked) return;
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const handleInventorySelect = (item) => {
    if (fieldsLocked) return;
    setFormData((prev) => ({
      ...prev,
      lineItems: [...(prev.lineItems || []), {
        itemNumber: item.itemNumber || '',
        code: item.salesTaxCode || '',
        description: item.description || item.itemNumber || '',
        unitPrice: parseFloat(item.price || item.cost || 0),
        quantity: 1,
        category: 'parts'
      }]
    }));
    setShowInventorySearch(false);
    setInventoryResetKey((k) => k + 1);
  };

  const buildSavePayload = () => ({
    ...formData,
    unlockEdit: unlockEdit || undefined
  });

  const markPrintedChecklist = async () => {
    onPrinted?.();
    if (!serviceCall?.id) return;
    try {
      await serviceCallsAPI.updateChecklist(serviceCall.id, { printed: true });
      queryClient.invalidateQueries(['serviceCall', serviceCall.id]);
      queryClient.invalidateQueries(['invoiceFolderItems']);
    } catch {
      // non-blocking
    }
  };

  const handleSave = () => {
    if (isVoid) {
      toast.error('Voided invoices cannot be edited');
      return;
    }
    if (financiallyLocked && !unlockEdit) {
      saveMutation.mutate({ invoiceNumber: formData.invoiceNumber || null });
      return;
    }
    saveMutation.mutate(buildSavePayload());
  };

  const handlePreview = async () => {
    try {
      if (!fieldsLocked) await saveMutation.mutateAsync(buildSavePayload());
      const html = await fetchInvoicePreviewHtml(formData.id);
      setPreviewHtml(html);
      setShowPreview(true);
    } catch {
      // handled by mutation
    }
  };

  const handlePrint = async () => {
    try {
      if (!fieldsLocked) await saveMutation.mutateAsync(buildSavePayload());
      await printInvoicePreview(formData.id);
      toast.success('Print dialog opened');
      await markPrintedChecklist();
    } catch (error) {
      console.error('Error printing invoice:', error);
      toast.error(error.message || 'Failed to print invoice');
    }
  };

  const handleDownloadPdf = async () => {
    try {
      if (!fieldsLocked) await saveMutation.mutateAsync(buildSavePayload());
      await downloadInvoicePdf(formData.id, formData.invoiceNumber);
      toast.success('PDF downloaded');
      await markPrintedChecklist();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleSend = async () => {
    if (isVoid) {
      toast.error('Cannot send a voided invoice');
      return;
    }
    try {
      if (!fieldsLocked) await saveMutation.mutateAsync(buildSavePayload());
      setShowSendModal(true);
    } catch {
      // handled by mutation
    }
  };

  const confirmSend = () => {
    const toEmails = recipientInput
      .split(/[,;\s]+/)
      .map((email) => email.trim())
      .filter(Boolean);
    if (!toEmails.length) {
      toast.error('Enter at least one recipient email');
      return;
    }
    sendMutation.mutate(toEmails);
  };

  const handleSendToContact = async () => {
    if (!contactEmail || isVoid) return;
    try {
      if (!fieldsLocked) await saveMutation.mutateAsync(buildSavePayload());
      setShowContactSendModal(true);
    } catch {
      // handled by mutation
    }
  };

  const confirmSendToContact = () => {
    if (!contactEmail) return;
    sendMutation.mutate([contactEmail], {
      onSuccess: () => setShowContactSendModal(false)
    });
  };

  const handleSuggestNextNumber = async () => {
    setSuggestingNumber(true);
    try {
      const res = await invoicesAPI.getNextNumber();
      updateField('invoiceNumber', res.data?.invoiceNumber || '');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to suggest invoice number');
    } finally {
      setSuggestingNumber(false);
    }
  };

  const handleMarkPaid = () => {
    if (!window.confirm('Mark this invoice as paid?')) return;
    statusMutation.mutate('paid');
  };

  const handleVoid = () => {
    if (!window.confirm('Void this invoice? Financial fields will stay locked.')) return;
    statusMutation.mutate('void');
  };

  const handleUnlock = () => {
    if (!window.confirm('Unlock financial fields to edit this sent invoice? Save will rewrite line items and totals.')) return;
    setUnlockEdit(true);
  };

  const inputClass = `block w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${fieldsLocked ? 'bg-gray-100 text-gray-600' : ''}`;
  const labelClass = 'block text-xs font-medium text-gray-600 mb-0.5';

  const editorBody = (
    <div className={inline ? '' : 'relative top-6 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white mb-10'}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{inline ? 'C Spire Invoice' : 'Edit C Spire Invoice'}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Status: <span className="font-medium capitalize">{formData.status || 'draft'}</span>
                {formData.sentAt ? ` · Sent ${new Date(formData.sentAt).toLocaleString()}` : ''}
                {fieldsLocked ? ' · Financial fields locked' : ''}
                {unlockEdit ? ' · Editing unlocked' : ''}
              </p>
            </div>
            <div className="flex items-center flex-wrap justify-end gap-2">
              {!isVoid && (
                <button
                  onClick={handleSave}
                  disabled={saveMutation.isLoading}
                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center space-x-1 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  <span>{saveMutation.isLoading ? 'Saving...' : 'Save'}</span>
                </button>
              )}
              <button
                onClick={handlePreview}
                disabled={saveMutation.isLoading}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-1 disabled:opacity-50"
              >
                <Eye className="h-4 w-4" />
                <span>Preview</span>
              </button>
              <button
                onClick={handleDownloadPdf}
                disabled={saveMutation.isLoading}
                className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center space-x-1 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                <span>PDF</span>
              </button>
              <button
                onClick={handlePrint}
                className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center space-x-1"
              >
                <Printer className="h-4 w-4" />
                <span>Print</span>
              </button>
              {!isVoid && (
                <>
                  <button
                    onClick={handleSend}
                    disabled={saveMutation.isLoading || sendMutation.isLoading}
                    className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center space-x-1 disabled:opacity-50"
                  >
                    <Mail className="h-4 w-4" />
                    <span>Send</span>
                  </button>
                  <button
                    onClick={handleSendToContact}
                    disabled={!contactEmail || saveMutation.isLoading || sendMutation.isLoading}
                    title={contactEmail ? `Send to ${contactName || contactEmail} (${contactEmail})` : 'No C Spire contact on file for this work order'}
                    className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center space-x-1 disabled:opacity-50 disabled:bg-gray-300"
                  >
                    <Send className="h-4 w-4" />
                    <span>Send to {contactName ? contactName.split(' ')[0] : 'C Spire Contact'}</span>
                  </button>
                </>
              )}
              {formData.status !== 'paid' && !isVoid && (
                <button
                  type="button"
                  onClick={handleMarkPaid}
                  disabled={statusMutation.isLoading}
                  className="px-3 py-1.5 text-sm bg-teal-700 text-white rounded hover:bg-teal-800 flex items-center space-x-1 disabled:opacity-50"
                >
                  <CircleDollarSign className="h-4 w-4" />
                  <span>Mark paid</span>
                </button>
              )}
              {!isVoid && (
                <button
                  type="button"
                  onClick={handleVoid}
                  disabled={statusMutation.isLoading}
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 flex items-center space-x-1 disabled:opacity-50"
                >
                  <Ban className="h-4 w-4" />
                  <span>Void</span>
                </button>
              )}
              {financiallyLocked && !unlockEdit && !isVoid && (
                <button
                  type="button"
                  onClick={handleUnlock}
                  className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center space-x-1"
                >
                  <Unlock className="h-4 w-4" />
                  <span>Unlock edit</span>
                </button>
              )}
              {!inline && (
                <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {formData.sendLogs?.length > 0 && (
            <div className="mb-4 bg-indigo-50 border border-indigo-100 rounded p-3 text-xs text-indigo-900">
              <div className="font-semibold mb-1">Send history</div>
              {formData.sendLogs.slice(0, 3).map((log) => (
                <div key={log.id}>
                  {new Date(log.sentAt).toLocaleString()} → {(log.sentToEmails || []).join(', ')}
                  {' '}({log.status})
                </div>
              ))}
            </div>
          )}

          <div className="space-y-4 max-h-[calc(100vh-160px)] overflow-y-auto pr-1">
            <div className="bg-gray-50 p-3 rounded-md">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Invoice Info</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Invoice Number</label>
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={formData.invoiceNumber || ''}
                      onChange={(e) => updateField('invoiceNumber', e.target.value)}
                      disabled={isVoid}
                      className={inputClass}
                    />
                    {!isVoid && (
                      <button
                        type="button"
                        onClick={handleSuggestNextNumber}
                        disabled={suggestingNumber}
                        className="px-2 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 whitespace-nowrap"
                      >
                        {suggestingNumber ? '…' : 'Next #'}
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Invoice Date</label>
                  <input
                    type="text"
                    value={formData.invoiceDate || ''}
                    onChange={(e) => updateField('invoiceDate', e.target.value)}
                    disabled={fieldsLocked}
                    className={inputClass}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-700">Bill To</h4>
                  {!fieldsLocked && (
                    <button
                      onClick={addBillToLine}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-0.5"
                    >
                      <Plus className="h-3 w-3" />
                      <span>Add Line</span>
                    </button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {(formData.billTo || []).map((line, i) => (
                    <div key={i} className="flex items-center space-x-1">
                      <input
                        type="text"
                        value={line}
                        onChange={(e) => updateBillToLine(i, e.target.value)}
                        disabled={fieldsLocked}
                        className={inputClass}
                      />
                      {!fieldsLocked && formData.billTo.length > 1 && (
                        <button onClick={() => removeBillToLine(i)} className="text-red-400 hover:text-red-600 flex-shrink-0">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 p-3 rounded-md">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Site Location</h4>
                <div className="space-y-1.5">
                  {[
                    ['locationName', 'Location Name'],
                    ['siteId', 'Site ID'],
                    ['address', 'Address'],
                    ['cityStateZip', 'City, State, Zip'],
                    ['openedBy', 'Opened By']
                  ].map(([field, label]) => (
                    <div key={field}>
                      <label className={labelClass}>{label}</label>
                      <input
                        type="text"
                        value={formData.siteLocation?.[field] || ''}
                        onChange={(e) => updateSiteField(field, e.target.value)}
                        disabled={fieldsLocked}
                        className={inputClass}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-md">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Work Order & Generator</h4>
              <div className="grid grid-cols-3 gap-3">
                {[
                  ['workOrderNumber', 'Work Order #'],
                  ['generatorModel', 'Generator Model'],
                  ['generatorSerialNumber', 'Generator Serial #']
                ].map(([field, label]) => (
                  <div key={field}>
                    <label className={labelClass}>{label}</label>
                    <input
                      type="text"
                      value={formData[field] || ''}
                      onChange={(e) => updateField(field, e.target.value)}
                      disabled={fieldsLocked}
                      className={inputClass}
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                {[
                  ['airFilter', 'Air filter'],
                  ['fuelFilter1', 'Fuel filter 1'],
                  ['fuelFilter2', 'Fuel filter 2'],
                  ['oilFilter1', 'Oil filter 1'],
                  ['oilFilter2', 'Oil filter 2']
                ].map(([field, label]) => (
                  <div key={field}>
                    <label className={labelClass}>{label}</label>
                    <input
                      type="text"
                      value={formData[field] || ''}
                      onChange={(e) => updateField(field, e.target.value)}
                      disabled={fieldsLocked}
                      className={inputClass}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-md">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Service Details</h4>
              <div className="grid grid-cols-3 gap-3">
                {[
                  ['serviceDate', 'Date of Service'],
                  ['hoursRun', 'Hours Run'],
                  ['serviceType', 'Type of Service']
                ].map(([field, label]) => (
                  <div key={field}>
                    <label className={labelClass}>{label}</label>
                    <input
                      type="text"
                      value={formData[field] || ''}
                      onChange={(e) => updateField(field, e.target.value)}
                      disabled={fieldsLocked}
                      className={inputClass}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-md">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Work Performed</h4>
              <textarea
                value={formData.workPerformed || ''}
                onChange={(e) => updateField('workPerformed', e.target.value)}
                rows={4}
                disabled={fieldsLocked}
                className={`${inputClass} resize-y`}
              />
            </div>

            <div className="bg-gray-50 p-3 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700">Line Items</h4>
                {!fieldsLocked && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowInventorySearch(!showInventorySearch)}
                      className="text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700 flex items-center space-x-0.5"
                    >
                      <Search className="h-3 w-3" />
                      <span>From Inventory</span>
                    </button>
                    <button
                      onClick={addLineItem}
                      className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 flex items-center space-x-0.5"
                    >
                      <Plus className="h-3 w-3" />
                      <span>Add Item</span>
                    </button>
                  </div>
                )}
              </div>
              {showInventorySearch && !fieldsLocked && (
                <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded p-3">
                  <InventorySearch
                    onSelect={handleInventorySelect}
                    placeholder="Search parts by item # or description..."
                    resetKey={inventoryResetKey}
                  />
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-300 text-left">
                      <th className="pb-1 pr-2 text-xs font-medium text-gray-600 w-28">Item #</th>
                      <th className="pb-1 pr-2 text-xs font-medium text-gray-600 w-20">Code</th>
                      <th className="pb-1 pr-2 text-xs font-medium text-gray-600">Description</th>
                      <th className="pb-1 pr-2 text-xs font-medium text-gray-600 w-24 text-right">Unit Price</th>
                      <th className="pb-1 pr-2 text-xs font-medium text-gray-600 w-16 text-right">Qty</th>
                      <th className="pb-1 pr-2 text-xs font-medium text-gray-600 w-24 text-right">Amount</th>
                      <th className="pb-1 pr-2 text-xs font-medium text-gray-600 w-24">Category</th>
                      <th className="pb-1 pr-2 text-xs font-medium text-gray-600 w-24">Tax</th>
                      <th className="pb-1 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(formData.lineItems || []).map((item, i) => {
                      const amount = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 1);
                      return (
                        <tr key={i} className="border-b border-gray-200">
                          <td className="py-1 pr-2">
                            <input type="text" value={item.itemNumber || ''} onChange={(e) => updateLineItem(i, 'itemNumber', e.target.value)} disabled={fieldsLocked} className={inputClass} />
                          </td>
                          <td className="py-1 pr-2">
                            <input type="text" value={item.code || ''} onChange={(e) => updateLineItem(i, 'code', e.target.value)} disabled={fieldsLocked} className={inputClass} />
                          </td>
                          <td className="py-1 pr-2">
                            <input type="text" value={item.description || ''} onChange={(e) => updateLineItem(i, 'description', e.target.value)} disabled={fieldsLocked} className={inputClass} />
                          </td>
                          <td className="py-1 pr-2">
                            <input type="number" step="0.01" value={item.unitPrice ?? 0} onChange={(e) => updateLineItem(i, 'unitPrice', e.target.value)} disabled={fieldsLocked} className={`${inputClass} text-right`} />
                          </td>
                          <td className="py-1 pr-2">
                            <input type="number" step="1" value={item.quantity ?? 1} onChange={(e) => updateLineItem(i, 'quantity', e.target.value)} disabled={fieldsLocked} className={`${inputClass} text-right`} />
                          </td>
                          <td className="py-1 pr-2 text-right font-medium text-gray-700">{formatMoney(amount)}</td>
                          <td className="py-1 pr-2">
                            <select
                              value={item.category || 'other'}
                              onChange={(e) => updateLineItem(i, 'category', e.target.value)}
                              disabled={fieldsLocked}
                              className={`${inputClass} text-xs`}
                            >
                              {LINE_ITEM_CATEGORIES.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="py-1 pr-2">
                            <select
                              value={item.taxable || ''}
                              onChange={(e) => updateLineItem(i, 'taxable', e.target.value || null)}
                              disabled={fieldsLocked}
                              className={`${inputClass} text-xs`}
                            >
                              {LINE_ITEM_TAXABLE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="py-1">
                            {!fieldsLocked && (
                              <button onClick={() => removeLineItem(i)} className="text-red-400 hover:text-red-600">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 pt-2 border-t border-gray-300">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={taxEnabled}
                        onChange={(e) => updateField('taxEnabled', e.target.checked)}
                        disabled={fieldsLocked}
                        className="rounded border-gray-300"
                      />
                      <span>Apply sales tax</span>
                    </label>
                    {taxEnabled && (
                      <div className="flex items-center space-x-2">
                        <label className={labelClass}>Tax Rate (%)</label>
                        <input
                          type="number"
                          step="0.1"
                          value={((formData.taxRate ?? 0.07) * 100).toFixed(1)}
                          onChange={(e) => updateField('taxRate', parseFloat(e.target.value) / 100 || 0)}
                          disabled={fieldsLocked}
                          className="w-20 rounded border border-gray-300 px-2 py-1 text-sm text-right focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                      </div>
                    )}
                  </div>
                  <div className="text-sm space-y-0.5 text-right">
                    <div className="text-gray-600">
                      Subtotal: <span className="font-medium text-gray-800 ml-2">{formatMoney(subtotal)}</span>
                    </div>
                    {taxEnabled && (
                      <div className="text-gray-600">
                        Tax ({(taxRate * 100).toFixed(1)}%): <span className="font-medium text-gray-800 ml-2">{formatMoney(taxAmount)}</span>
                      </div>
                    )}
                    <div className="text-gray-900 font-semibold text-base">
                      Grand Total: <span className="ml-2">{formatMoney(grandTotal)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
    </div>
  );

  return (
    <>
      {inline ? (
        editorBody
      ) : (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-[60]">
          {editorBody}
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h4 className="font-semibold text-gray-900">Invoice Preview</h4>
              <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <iframe title="Invoice preview" srcDoc={previewHtml} className="flex-1 w-full border-0" />
          </div>
        </div>
      )}

      {showSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Send Invoice</h4>
            <p className="text-sm text-gray-600 mb-3">
              Enter one or more recipient emails (comma-separated). The invoice PDF will be attached.
            </p>
            <textarea
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              rows={3}
              className="block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              placeholder="email@example.com, billing@example.com"
            />
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={() => setShowSendModal(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmSend}
                disabled={sendMutation.isLoading}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
              >
                {sendMutation.isLoading ? 'Sending...' : 'Send Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showContactSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Send Invoice to C Spire Contact</h4>
            <p className="text-sm text-gray-600 mb-3">
              The invoice PDF will be emailed directly to the contact captured from this work order's C Spire email
              {workOrder?.openedByAction && workOrder.openedByAction.toLowerCase() !== 'opened'
                ? ` (via "${workOrder.openedByAction} By")`
                : ''}.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm space-y-0.5">
              <div className="font-medium text-gray-900">{contactName || 'Unnamed contact'}</div>
              <div className="text-gray-700">{contactEmail}</div>
              {contactPhone && <div className="text-gray-500">{contactPhone}</div>}
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={() => setShowContactSendModal(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmSendToContact}
                disabled={sendMutation.isLoading}
                className="px-4 py-2 text-sm text-white bg-emerald-600 rounded hover:bg-emerald-700 disabled:opacity-50"
              >
                {sendMutation.isLoading ? 'Sending...' : `Send to ${contactName || contactEmail}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
